import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { ValeurImposee } from './liens.js';
/* LE LIEN VIENT DU CONTRAT, EN TYPE SEULEMENT : une intention d'ajout d'ENFANT
   porte le lien de la carte qui l'a posee, et c'est ce lien-la que
   `nouvelEnfant` relira pour ranger l'enfant dans le bon jeu. Aucun import de
   valeur : `views/contract.ts` ne produit rien a l'execution. */
import type { LienParLeChampLie } from '../views/contract.js';

/**
 * Action d'app en attente : le SHELL la pose quand l'utilisateur declenche un
 * shortcut d'app depuis le dock (ex. "Nouveau contact") ; l'APP la consomme au
 * montage. Circuit type cote app, dans un useEffect : lire `actionId` via
 * usePendingAppAction, ouvrir le Modal correspondant, puis appeler `consume()`
 * pour marquer l'action traitee (le shell la remet a null).
 *
 * DEPUIS LES LIENS ENTRE PORTES, elle porte aussi de quoi CREER UNE FICHE LIEE
 * depuis la fiche parente : le « + Ajouter » d'une carte liee PASSE dans la
 * porte liee et y ouvre SA creation, le champ du lien pre-rempli. La fenetre de
 * creation appartient a l'ECRAN de l'app (le socle n'en a aucune), donc ce qui
 * voyage n'est pas une fenetre, c'est une INTENTION : quelle porte, et quelles
 * valeurs d'office.
 *
 * ELLE VIT EN MEMOIRE, PAS DANS L'ADRESSE : c'est un geste en cours, pas un
 * etat qu'on partage ou qu'on recharge. Une adresse qui porterait « cree une
 * intervention pour m1 » rouvrirait la fenetre a chaque retour navigateur.
 */
export interface PendingAppAction {
  /**
   * Id de l'app visee (celui du registre / installedApps).
   *
   * IL EST POSE DANS LES DEUX SENS DEPUIS LA RELECTURE DU 09/09/2026. Un
   * raccourci du dock le porte parce que le SHELL vise une app ; une intention
   * du socle (« + Ajouter » d'une carte liee) le porte parce que l'ECRAN MONTE
   * l'a declare (`useDeclarerAppDeLEcran`, appele par `useScreen` avec l'`appId`
   * de ses options). Sans lui, une intention nommant la porte « interventions »
   * pouvait etre consommee par n'importe quelle app ayant une porte de ce nom :
   * le geste ouvrait une creation dans le mauvais jeu, sans un mot.
   *
   * CE N'EST PAS LUI QUI DIT QUI CONSOMME : c'est la PORTE (voir
   * `destinataireDeLIntention`). Une intention qui nomme une porte est pour le
   * GABARIT, meme quand elle porte aussi une app ; l'app, elle, ne voit que les
   * raccourcis du dock.
   */
  appId?: string;
  /** Id de l'action declaree par l'app (champ `actions` du registre). */
  actionId: string;
  /**
   * LA PORTE VISEE, quand l'intention en designe une : celle qui ouvrira sa
   * creation. Absente pour un raccourci du dock, qui vise l'app et la laisse
   * choisir.
   */
  door?: string;
  /**
   * LE MOTEUR PASSE LUI-MEME DANS LA PORTE VISEE : c'est un raccourci du dock
   * qui nomme une porte (`raccourcis.ts` de `@taki/shell`). Le « + Ajouter »
   * d'une carte liee ne le pose pas, il change de porte lui-meme.
   *
   * POURQUOI PAS L'ADRESSE, quand l'app est deja ouverte : le shell ne sait pas
   * quelle porte l'ecran montre, et une adresse `?s=<porte>` qui arrive APRES
   * l'ouverture du brouillon le refermerait (une adresse sans `id` ferme la
   * fiche ouverte). L'ecran, lui, connait sa porte active, et son changement de
   * porte passe par la garde du brouillon.
   */
  changerDePorte?: true;
  /**
   * CE QUE LA CREATION RECOIT D'OFFICE : le champ du lien, l'id de la fiche
   * parente, et le titre a montrer dans la case verrouillee.
   */
  defaults?: ValeurImposee[];
  /**
   * LA FICHE VIDE AJOUTERA UN ENFANT A UN MODELE, PAS UNE FICHE REELLE (pivot
   * du 10/09/2026).
   *
   * POURQUOI L'INTENTION LE PORTE. Le « + Ajouter » d'une carte liee fait
   * exactement le meme geste dans les deux cas : il PASSE dans la porte liee et
   * y ouvre le brouillon. Ce qui change est ce que « Creer » fera au bout, et
   * seule la CARTE le sait au moment du clic (elle a `ViewProps.modele`) : a
   * l'arrivee, la porte liee n'a plus aucun moyen de deviner qu'on vient d'un
   * modele. Sans ce champ, « Creer » creerait une vraie intervention, accrochee
   * a un `assetId` qui est en realite l'id d'un MODELE : une fiche fantome que
   * plus aucune carte ne montre.
   *
   * ABSENT EST LE CAS ORDINAIRE (le « + Ajouter » d'une vraie fiche parente).
   */
  enfantDe?: {
    /** L'id du modele qui recevra l'enfant. */
    modele: string;
    /** Le lien de la carte : famille, sous-type et chemin du champ de lien. */
    lien: LienParLeChampLie;
    /** Le NOM du modele parent : ce que la case verrouillee affiche. */
    libelle: string;
  };
  /**
   * LA FICHE CREEE REMPLIRA CE CHAMP (chantier `champ-lie-deux-gestes`) : le
   * « Ajouter » d'un CHAMP de lien. Une fois « Creer » tenu, le moteur ecrit
   * l'id de la fiche neuve sur la fiche d'origine, puis y revient.
   *
   * ABSENT EST LE CAS ORDINAIRE, et il exclut `enfantDe` : un enfant de modele
   * n'a pas d'id de fiche a rendre.
   */
  retour?: RetourDeCreation;
}

/**
 * OU RENDRE L'ID D'UNE FICHE CREEE DEPUIS UN CHAMP DE LIEN : la fiche
 * d'origine, et le chemin de son champ.
 *
 * L'ID D'ORIGINE VOYAGE DANS L'INTENTION, et c'est ce qui rend l'ecriture sure :
 * une intention mangee par un autre geste n'ecrit rien, et deux onglets ne
 * partagent pas la meme.
 */
export interface RetourDeCreation {
  /** Le jeu de la fiche d'origine (celle dont le champ recevra l'id). */
  family: string;
  type?: string;
  /** Son id. */
  id: string;
  /** Le CHEMIN DE STOCKAGE du champ (`contactId`, `customFields.clientId`). */
  chemin: string;
  /**
   * LE CHAMP EST UNE LISTE (le sens direct d'un lien, ou des liens multiples) :
   * l'id cree s'AJOUTE a la liste de la fiche d'origine (`LiensPort.ajouterALaListe`)
   * au lieu de la REMPLACER (`rattacher`). Sans cette clef, la creation depuis la
   * carte d'une liste ecraserait les fiches deja citees.
   */
  liste?: true;
  /** Ce que la pastille nomme si l'ecriture echoue : la fiche, puis le champ. */
  titre: string;
  libelleDuChamp: string;
}

/**
 * QUI CONSOMME UNE INTENTION : LA REGLE, ECRITE UNE FOIS.
 *
 * LA PORTE TRANCHE. Une intention qui NOMME UNE PORTE appartient au GABARIT :
 * c'est lui qui connait la porte active et son action « add », et c'est lui qui
 * appelle cette action avec les valeurs imposees a l'arrivee
 * (`views/useScreen.ts`). Une intention SANS porte reste celle de l'APP : c'est
 * le raccourci du dock, qui vise l'app et la laisse choisir ce qu'elle ouvre.
 *
 * L'APP, ELLE, CADRE ENCORE : une intention de porte qui nomme AUSSI une app
 * n'est pour le gabarit que d'un ecran de CETTE app. Deux apps peuvent avoir
 * une porte du meme nom (« interventions », « lignes », « taches ») ; sans ce
 * second cadrage, un « + Ajouter » pose dans l'une ouvrirait la creation de
 * l'autre, sur un autre jeu, et rien a l'ecran ne le dirait. `appId` absent des
 * DEUX cotes = on ne cadre rien, exactement comme avant.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : les deux chemins se croisent dans le meme
 * rendu (le moteur d'ecran est un hook de l'ecran de l'app), et rien a
 * l'ecran ne dirait qu'un des deux a mange l'intention de l'autre : la fenetre
 * s'ouvrirait, simplement vide.
 *
 * @param appId L'app de l'ecran qui pose la question. Absent : on ne cadre pas.
 */
export function destinataireDeLIntention(
  intention: PendingAppAction | null,
  appId?: string,
): 'gabarit' | 'app' | null {
  if (intention == null) return null;
  if (intention.door == null || intention.door === '') return 'app';
  if (intention.appId != null && appId !== undefined && intention.appId !== appId) return null;
  return 'gabarit';
}

/** Valeur fournie par le shell au provider. */
export interface PendingActionContextValue {
  /** L'action en attente, ou null quand il n'y a rien a traiter. */
  pending: PendingAppAction | null;
  /** Marque l'action traitee : le shell remet `pending` a null. */
  consume: () => void;
}

/**
 * CE QUE LE CONTEXTE PORTE VRAIMENT : l'intention du SHELL, plus celle que le
 * SOCLE a pu poser lui-meme, plus les deux gestes qui vont avec.
 *
 * DEUX POSEURS, UN SEUL LECTEUR PAR DESTINATAIRE. Le shell pose les raccourcis
 * du dock (il tient leur etat, et son `consume` les efface chez lui) ; le socle
 * pose le « + Ajouter » d'une carte liee, et cet etat-la vit ICI, dans le
 * fournisseur, parce que rien au-dessus ne le connait. Le shell n'a donc pas
 * une ligne a changer, et son comportement ne bouge pas d'un cheveu.
 */
interface EtatDIntention extends PendingActionContextValue {
  /** POSER une intention depuis le socle (« + Ajouter » d'une carte liee). */
  poser: (intention: PendingAppAction) => void;
  /**
   * L'ECRAN MONTE DECLARE SON APP, et c'est elle qui SIGNE les intentions que
   * le socle pose ensuite. Le socle ne connait pas l'id de l'app depuis une
   * carte liee (aucune vue ne le recoit, et le contrat n'a pas a le porter pour
   * ca) : c'est le moteur d'ecran qui l'a, dans ses options.
   */
  declarerApp: (appId: string) => void;
}

const PendingActionContext = createContext<EtatDIntention>({
  pending: null,
  consume: () => {},
  declarerApp: () => {},
  /* SANS FOURNISSEUR, POSER NE FAIT RIEN, ET LE DIT EN DEVELOPPEMENT. Le cas
     existe (banc d'essai, test, hote sans shell) : le geste est alors sans suite, et
     un « + Ajouter » qui change de porte sans ouvrir la creation ressemble a un
     bug de l'app plutot qu'a un hote incomplet. */
  poser: (intention) => {
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/actions] Intention "${intention.actionId}" perdue : aucun PendingActionProvider ` +
          "au-dessus de cet ecran. Le socle ne peut pas demander sa creation a la porte visee.",
      );
    }
  },
});

/**
 * Fournisseur pose par le SHELL autour des ecrans d'app (contexte, pour ne pas
 * cabler chaque app). Les apps ne l'utilisent jamais directement : elles
 * passent par usePendingAppAction.
 *
 * IL TIENT AUSSI L'INTENTION DU SOCLE, et c'est la seule chose qu'il ajoute a
 * ce que le shell lui passe : le « + Ajouter » d'une carte liee doit SURVIVRE
 * au changement de porte (l'ecran ne se demonte pas, mais la carte, elle, part
 * avec la fiche). Ce fournisseur est monte au-dessus des routes d'app : c'est
 * le premier endroit qui reste en place pendant tout le trajet.
 *
 * LA PLUS RECENTE GAGNE : une intention posee par le socle passe devant celle
 * du shell. Les deux ne coexistent pas dans la vraie vie (on ne clique pas un
 * raccourci du dock et un « + Ajouter » dans le meme geste), et devant deux
 * gestes il faut bien servir le dernier.
 */
export function PendingActionProvider({
  value,
  children,
}: {
  value: PendingActionContextValue;
  children: ReactNode;
}): JSX.Element {
  const [locale, setLocale] = useState<PendingAppAction | null>(null);
  const externe = value.pending;
  const consumeExterne = value.consume;

  /* L'APP DE L'ECRAN MONTE, en REF et pas en etat : elle ne change rien a
     l'affichage, elle SIGNE la prochaine intention. Un etat aurait re-rendu
     toutes les routes d'app a chaque montage d'ecran, pour rien. */
  const appDeLEcran = useRef<string | null>(null);
  const declarerApp = useCallback((appId: string): void => {
    appDeLEcran.current = appId;
  }, []);

  const poser = useCallback((intention: PendingAppAction): void => {
    /* SIGNEE PAR L'ECRAN QUI L'A POSEE, quand il s'est declare. Une intention
       qui arrive deja signee (elle ne vient alors pas du socle) passe telle
       quelle : on ne recrit jamais ce que l'appelant a dit. */
    setLocale(
      intention.appId === undefined && appDeLEcran.current != null
        ? { ...intention, appId: appDeLEcran.current }
        : intention,
    );
  }, []);

  const consume = useCallback((): void => {
    // LES DEUX D'UN COUP : celui qui consomme ne sait pas d'ou l'intention
    // vient, et n'a pas a le savoir. Effacer l'une en laissant l'autre ferait
    // repartir un geste deja traite au rendu suivant.
    setLocale(null);
    consumeExterne();
  }, [consumeExterne]);

  const etat = useMemo<EtatDIntention>(
    () => ({ pending: locale ?? externe, consume, poser, declarerApp }),
    [locale, externe, consume, poser, declarerApp],
  );

  return <PendingActionContext.Provider value={etat}>{children}</PendingActionContext.Provider>;
}

/**
 * Cote APP : lit l'action en attente destinee a CETTE app. `actionId` n'est
 * non null que si l'action vise `appId` ; les actions des autres apps restent
 * invisibles, et une intention SANS `appId` (posee par le socle, adressee a une
 * PORTE) l'est aussi. Appeler `consume()` une fois l'action prise en charge.
 *
 * L'INTENTION ENTIERE EST RENDUE (`action`), pas seulement son id : depuis les
 * liens entre portes, elle peut designer une PORTE et porter des VALEURS
 * IMPOSEES, et une app qui cree une fiche liee a besoin des trois.
 *
 * `actionId` RESTE, ET IL RESTE EN PREMIER : les appelants d'avant ce lot ne
 * lisent que lui (`const { actionId, consume } = usePendingAppAction(id)`), et
 * un champ de plus dans l'objet rendu ne leur demande rien. Le remplacer aurait
 * fait reecrire chaque app pour un besoin qu'elles n'ont pas.
 */
export function usePendingAppAction(appId: string): {
  actionId: string | null;
  /** L'intention complete (porte visee, valeurs imposees), ou `null`. */
  action: PendingAppAction | null;
  consume: () => void;
} {
  const { pending, consume } = useContext(PendingActionContext);
  /* L'APP NE VOIT QUE CE QUI N'EST PAS ADRESSE A UNE PORTE, et c'est la REGLE
     unique qui le dit. Depuis que le socle SIGNE ses intentions, l'`appId` seul
     ne suffit plus a les distinguer : une intention de porte porte desormais la
     meme app que le raccourci du dock, et l'app l'aurait mangee au gabarit. */
  const pourLApp = destinataireDeLIntention(pending) === 'app';
  const action = pourLApp && pending != null && pending.appId === appId ? pending : null;
  return { actionId: action?.actionId ?? null, action, consume };
}

/**
 * L'ECRAN MONTE DECLARE SON APP au fournisseur d'intentions.
 *
 * APPELE PAR `useScreen`, ET PAR PERSONNE D'AUTRE : c'est le seul endroit qui
 * connaisse l'`appId` (ses options) ET qui vive sous le fournisseur. Les
 * intentions que le socle pose ensuite (« + Ajouter » d'une carte liee) en sont
 * signees, ce qui les empeche d'etre consommees par l'ecran d'une AUTRE app
 * dont une porte porterait le meme nom.
 *
 * UNE REF, PAS UN ETAT (voir `PendingActionProvider`) : declarer une app ne
 * change rien a l'ecran, ca change ce que la prochaine intention portera.
 */
export function useDeclarerAppDeLEcran(appId: string): void {
  const { declarerApp } = useContext(PendingActionContext);
  useEffect(() => {
    declarerApp(appId);
  }, [declarerApp, appId]);
}

/**
 * Cote SOCLE : POSER une intention. C'est le « + Ajouter » d'une carte liee, et
 * rien d'autre aujourd'hui.
 *
 * IL NE POSE PAS D'`appId` LUI-MEME, et il ne le peut pas : une carte liee ne
 * connait pas l'id de l'app (aucune vue ne le recoit, et le contrat n'a pas a
 * le porter pour ca). Elle nomme une PORTE. C'est le FOURNISSEUR qui SIGNE, avec
 * l'app que l'ecran monte a declaree (`useDeclarerAppDeLEcran`) : la carte n'a
 * rien a savoir, et l'intention ne peut plus atterrir dans une autre app.
 */
export function usePendingAppActionSetter(): (intention: PendingAppAction) => void {
  return useContext(PendingActionContext).poser;
}

/**
 * Cote GABARIT : l'intention adressee a CETTE porte, et de quoi la consommer.
 *
 * Elle n'est rendue que quand la porte visee est celle qu'on montre : tant
 * qu'on n'est pas arrive, l'intention attend, et c'est exactement ce qu'on veut
 * (elle est posee AVANT le changement de porte, dans le meme geste).
 */
export function usePendingDoorAction(
  doorId: string,
  /** L'app de l'ecran : une intention signee d'une AUTRE app n'est pas pour lui. */
  appId?: string,
): {
  action: PendingAppAction | null;
  consume: () => void;
} {
  const { pending, consume } = useContext(PendingActionContext);
  const pourLeGabarit = destinataireDeLIntention(pending, appId) === 'gabarit';
  return {
    action: pourLeGabarit && pending?.door === doorId ? pending : null,
    consume,
  };
}

/**
 * Cote GABARIT : l'intention d'un raccourci du dock qui demande de passer dans
 * sa porte (`changerDePorte`), ou `null`.
 *
 * ELLE N'EST RENDUE QUE POUR UNE INTENTION DU GABARIT, de cet ecran (meme
 * cadrage que `usePendingDoorAction`). Une fois dans la porte, c'est
 * `usePendingDoorAction` qui prend le relais et ouvre la creation.
 */
export function usePendingDoorTarget(appId?: string): PendingAppAction | null {
  const { pending } = useContext(PendingActionContext);
  if (destinataireDeLIntention(pending, appId) !== 'gabarit') return null;
  return pending?.changerDePorte === true && pending.door != null ? pending : null;
}
